// mercado-pago.strategy.ts
import { Injectable } from '@angular/core';
import { PaymentStrategy, PaymentResult } from '../payment-strategy.interface';
import { MercadoPagoService } from '../services/mercado-pago.service';
import { PaymentApiService } from '../services/payment-api.service';

/**
 * Cobro con tarjeta vía Mercado Pago, en ARS (moneda de liquidación del backend).
 *
 * Usa el flujo de agencia de la Booking API: POST paymentMasters/create con
 * payment_platform_id 2. El backend recalcula el importe contra los productos, suma el
 * fee de la plataforma y cobra; el importe que manda el front solo se compara.
 */
@Injectable()
export class MercadoPagoStrategy implements PaymentStrategy {

  /** payment_platform_id de Mercado Pago en la Booking API */
  static readonly PLATFORM_ID = 2;
  /** Moneda en la que cobra Mercado Pago (MERCADO_PAGO_SETTLEMENT_CURRENCY) */
  static readonly CURRENCY = 'ARS';

  constructor(private mercadoPagoService: MercadoPagoService, private paymentApiService: PaymentApiService) {}

  async initialize(config?: any): Promise<boolean> {
    return await this.mercadoPagoService.initialize();
  }

  async renderPaymentForm(containerId: string, options?: { amount: number; payerEmail?: string }): Promise<boolean> {
    return await this.mercadoPagoService.renderCardForm(containerId, options?.amount, options?.payerEmail);
  }

  async processPayment(amount: number, metadata: any): Promise<PaymentResult> {
    try {
      // 1. Tokenizar la tarjeta (el Brick valida los campos)
      const card = await this.mercadoPagoService.getCardData();
      if (!card) {
        return { success: false, error: 'Revisá los datos de la tarjeta.' };
      }

      // 2. Payload del flujo de agencia (ver "Pagos con Mercado Pago — Booking API")
      const mercadoPagoPayload = {
        paymentMethods: [{
          payment_methods_id: 2, // Obligatorio para Plataformas de pago
          payment_platform_id: MercadoPagoStrategy.PLATFORM_ID,
          itinerary_id: metadata.itinerary_id,
          agency_id: metadata.agency_id,
          agent_id: metadata.agent_id,
          client_id: metadata.client_id,
          user_id_create: metadata.agent_id,
          total_amount: metadata.total_amount,
          amount_payment: metadata.amount_payment,
          currency: MercadoPagoStrategy.CURRENCY,
          currency_payment: MercadoPagoStrategy.CURRENCY,
          token: card.token,
          payment_method_id: card.payment_method_id,
          issuer_id: card.issuer_id,
          installments: Number(card.installments) || 1,
          payer: {
            email: card.payer?.email || metadata.dataPaymentCreditCard?.payer_email,
            identification: card.payer?.identification
          },
          saved_card: false,
          paymentMasterDetails: metadata.payloadDetails
        }]
      };

      const response = await this.paymentApiService.createPayment(mercadoPagoPayload);

      // Las excepciones internas del backend también responden con success: false
      if (!response?.success) {
        return {
          success: false,
          error: this.getErrorMessage(response),
          message: response?.message
        };
      }

      const paymentMaster = response.paymentMaster || {};
      const approved = Number(paymentMaster.status_id) === 2;

      // 3. Registro del pago con tarjeta (mismo historial que Square/PayPal), recién con el
      //    cobro ya aceptado por Mercado Pago.
      try {
        await this.paymentApiService.savePaymetCreditsCards({
          payment_masters_id: paymentMaster.id ?? '',
          payment_id: paymentMaster.payment_platform_key,
          payer_id: metadata.dataPaymentCreditCard?.payer_id,
          payer_name: metadata.dataPaymentCreditCard?.payer_name,
          payer_email: mercadoPagoPayload.paymentMethods[0].payer.email,
          amount: paymentMaster.total_amount ?? metadata.total_amount,
          products: metadata.payloadDetails,
          status: approved ? 'APPROVED' : 'PENDING',
          currency: MercadoPagoStrategy.CURRENCY,
          agent_id: metadata.agent_id,
          agency_id: metadata.agency_id,
          itinerary_id: metadata.itinerary_id
        });
      } catch (error) {
        // El cobro ya está registrado en payments_masters; esto es solo el historial
        console.error('No se pudo registrar el pago con tarjeta de Mercado Pago:', error);
      }

      return {
        success: true,
        transactionId: paymentMaster.payment_platform_key,
        paymentId: paymentMaster.id != null ? String(paymentMaster.id) : undefined,
        message: approved
          ? 'Pago aprobado'
          : 'El pago está en proceso; se confirmará automáticamente.'
      };

    } catch (error: any) {
      console.error('❌ Error en MercadoPagoStrategy:', error);
      return {
        success: false,
        error: error?.message || 'Error procesando el pago con Mercado Pago'
      };
    }
  }

  /** Mensaje para el usuario según la respuesta de rechazo del backend / Mercado Pago */
  private getErrorMessage(response: any): string {
    const statusDetailMessages: { [statusDetail: string]: string } = {
      cc_rejected_insufficient_amount: 'La tarjeta no tiene fondos suficientes.',
      cc_rejected_bad_filled_security_code: 'El código de seguridad es incorrecto.',
      cc_rejected_bad_filled_date: 'La fecha de vencimiento es incorrecta.',
      cc_rejected_bad_filled_card_number: 'El número de tarjeta es incorrecto.',
      cc_rejected_bad_filled_other: 'Revisá los datos de la tarjeta.',
      cc_rejected_call_for_authorize: 'Tenés que autorizar el pago con el emisor de la tarjeta.',
      cc_rejected_card_disabled: 'La tarjeta no está habilitada. Comunicate con el emisor.',
      cc_rejected_duplicated_payment: 'Ya se realizó un pago por el mismo importe. Usá otra tarjeta o medio de pago.',
      cc_rejected_high_risk: 'El pago fue rechazado. Probá con otra tarjeta o medio de pago.',
      cc_rejected_max_attempts: 'Se alcanzó el límite de intentos. Probá con otra tarjeta.',
      cc_rejected_card_error: 'No se pudo procesar la tarjeta.',
      cc_rejected_blacklist: 'No se pudo procesar el pago.',
      cc_rejected_other_reason: 'El emisor de la tarjeta rechazó el pago.'
    };

    return statusDetailMessages[response?.status_detail]
      || response?.message
      || 'No se pudo procesar el pago con Mercado Pago.';
  }
}
